import React, { useEffect, useState } from 'react';
import MenuIcon from 'lucide-react/dist/esm/icons/menu';
import XIcon from 'lucide-react/dist/esm/icons/x';
import ChevronDownIcon from 'lucide-react/dist/esm/icons/chevron-down';
import ArrowUpIcon from 'lucide-react/dist/esm/icons/arrow-up';
import { useLocation, Link } from 'react-router-dom';
import { useIsMobile } from "@/hooks/use-mobile";
import DesktopFeaturesMenu from '@/components/DesktopFeaturesMenu';
import MobileDrawer from '@/components/MobileDrawer';

const GUTTER_PX = 100;
const MAX_W = 1400;

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const [showFeatures, setShowFeatures] = useState(false);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [lastScrollY, setLastScrollY] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection('up');
      }
      
      setScrolled(currentScrollY > 4);
      
      // Show scroll indicator on mobile after scrolling down 100px
      setLastScrollY(currentScrollY);
    };
    
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastScrollY, isMobile, scrollDirection]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname === path) return true;
    return false;
  };

  const isFeatureActive = () => {
    const featurePaths = ['/features/iphone-monitoring', '/features/hows-my-child-report', '/features/smart-alerts', '/features/contact-verification', '/features/analytics'];
    return featurePaths.includes(location.pathname);
  };

  const getNavigationState = (): 'top' | 'hidden' | 'visible' | 'mobile-visible' | 'mobile-hidden' => {
    if (isMobile) {
      if (!scrolled) return 'mobile-visible';
      if (scrollDirection === 'down' && window.scrollY > 100) return 'mobile-hidden';
      return 'mobile-visible';
    }
    if (!scrolled) return 'top';
    if (scrollDirection === 'down' && window.scrollY > 100) return 'hidden';
    return 'visible';
  };

  const navState = getNavigationState();

  useEffect(() => {
    // Lock body scroll when the mobile menu is open
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleCreateAccount = () => {
    const signupUrl = `${AUTH0_DOMAIN}/authorize?` +
      `client_id=${AUTH0_CLIENT_ID}&` +
      `response_type=code&` +
      `redirect_uri=${encodeURIComponent(APP_REDIRECT_URL)}&` +
      `scope=openid%20profile%20email&` +
      `prompt=login&` +
      `screen_hint=signup`;
    window.location.href = signupUrl;
  };

  const headerStyle: React.CSSProperties = (() => {
    switch (navState) {
      case 'top':
        return {
          top: 16,
          left: '50%',
          transform: 'translateX(-50%) translateY(0)',
          width: 'calc(100% - 32px)',
          borderRadius: 16,
          boxShadow: '0 4px 24px -4px rgba(0,0,0,0.1)',
          backgroundColor: '#ffffff'
        };
      case 'hidden':
        return {
          top: 24,
          left: '50%',
          transform: 'translateX(-50%) translateY(-100%)',
          width: 'calc(100% - 32px)',
          borderRadius: 16,
          boxShadow: '0 8px 32px -4px rgba(0,0,0,0.15)',
          backgroundColor: '#ffffff'
        };
      case 'visible':
        return {
          top: 24,
          left: '50%',
          transform: 'translateX(-50%) translateY(0)',
          width: 'calc(100% - 32px)',
          borderRadius: 16,
          boxShadow: '0 8px 32px -4px rgba(0,0,0,0.15)',
          backgroundColor: '#ffffff'
        };
      case 'mobile-visible':
        return {
          top: 12,
          left: '50%',
          transform: 'translateX(-50%) translateY(0)',
          width: 'calc(100% - 24px)',
          borderRadius: 12,
          boxShadow: '0 6px 24px -4px rgba(0,0,0,0.15)',
          backgroundColor: '#ffffff'
        };
      default:
        return {};
    }
  })();

  return (
    <header
      style={{
        ["--gutter" as any]: `${GUTTER_PX}px`,
        maxWidth: `${MAX_W}px`,
        width: "100%",
        position: 'fixed',
        zIndex: 50,
        transition: 'transform 0.3s ease, top 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
        ...headerStyle
      } as React.CSSProperties} 
      className={[
        "border", 
        "flex flex-col items-stretch justify-center", 
        `px-[100px] max-md:px-6`, 
        navState === "visible" || navState === "mobile-visible" ? "py-[11px] max-md:py-1" : "py-[21px] max-md:py-2", 
        "text-body text-joey-navy font-normal", 
        "max-w-full"
      ].join(" ")}
    >
      <nav className={`flex w-full items-center justify-between max-md:max-w-full ${navState === "visible" || navState === "mobile-visible" ? "min-h-[36px] max-md:min-h-[32px]" : "min-h-[52px] max-md:min-h-[36px]"}`}>
        <Link
          to="/"
          className="flex items-center gap-3 text-joey-navy text-subtitle font-medium hover:opacity-80 transition-opacity max-md:text-lg pt-4 pb-2"
        >
          <img
            src="/img/logo.png"
            alt="Joey Family logo"
            className="h-10 w-auto max-md:h-8"
            loading="lazy"
            decoding="async"
          />
          <span>JOEY</span>
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-4 absolute left-1/2 transform -translate-x-1/2">
          <Link to="/" className={`px-3 py-2 rounded-full transition-all duration-300 hover:bg-gray-100 whitespace-nowrap ${isActive('/') ? 'bg-joey-navy text-white' : 'text-joey-navy hover:opacity-80'}`}>
            Home
          </Link>

          <div className="relative">
            <DesktopFeaturesMenu />
          </div>

          <Link to="/about-us" className={`px-3 py-2 rounded-full transition-all duration-300 hover:bg-gray-100 whitespace-nowrap ${isActive('/about-us') ? 'bg-joey-navy text-white' : 'text-joey-navy hover:opacity-80'}`}>
            About Us
          </Link>
          <Link to="/articles" className={`px-3 py-2 rounded-full transition-all duration-300 hover:bg-gray-100 whitespace-nowrap ${isActive('/articles') ? 'bg-joey-navy text-white' : 'text-joey-navy hover:opacity-80'}`}>
            Articles
          </Link>
        </div>

        <div className="hidden lg:flex gap-4">
          <a href="https://app.joeyfamily.com/login" className="px-3 py-2 rounded-full transition-all duration-300 text-joey-navy bg-white border border-gray-300 hover:bg-gray-50 whitespace-nowrap">
            <span>Login</span>
          </a>
          <button onClick={handleCreateAccount} className="px-3 py-2 rounded-full transition-all duration-300 text-white bg-joey-navy hover:bg-joey-navy/90 whitespace-nowrap">
            <span>Get Started</span>
          </button>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden p-2 text-joey-navy hover:opacity-80 transition-opacity"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
        </button>
      </nav>

      {/* Mobile Navigation Menu (lazy) */}
      {isMenuOpen && <MobileDrawer onClose={() => setIsMenuOpen(false)} />}
    </header>
  );
};

export default Navigation;

function handleMenuToggle(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
	// typed event: safe to call e.preventDefault() and access currentTarget
}
